import React, { useCallback, useState } from 'react';

import { PremiumFeatureGateModal } from '../components/_molecules/BlockedModals';
import useAccessBadge from './useAccessBadge';
import { useMetrics } from './useMetrics';
import { usePaywallEmailTracking } from './usePaywallEmailTracking';

// State/region is kept on the metadata payload for analytics + BI continuity.
// The new modal itself only branches on US vs International — state-specific
// stat content ships inside the home/trial modals, not here.
type PaywallVariant = 'default' | 'TX' | 'CA';

function isPaywallVariant(value: string | undefined): value is PaywallVariant {
  return value === 'TX' || value === 'CA';
}

function usePaywallPrompt({
  startState,
  onClose,
}: {
  startState?: { screen: string };
  onClose?: () => void;
}) {
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const metrics = useMetrics();
  const trackPaywallEmail = usePaywallEmailTracking();
  const [show, setShow] = useState(false);

  const isReady = userAccessBadge !== undefined;

  const shouldPromptPaywall = useCallback(() => {
    return false;
  }, []);

  const prompt = useCallback(
    (metadata?: PaywallMetadata) => {
      const paywallVariant = metadata?.variant;
      const derivedVariant =
        paywallVariant && isPaywallVariant(paywallVariant) ? paywallVariant : 'default';

      metrics.logEvent('Paywall.Prompted', {
        ...metadata,
        screen: startState?.screen,
        state: derivedVariant,
      });
      setShow(true);
    },
    [metrics, startState?.screen],
  );

  const hide = useCallback(() => {
    setShow(false);
    onClose && onClose();
  }, [onClose]);

  const handleEmailSubmit = useCallback(
    (email: string) => {
      trackPaywallEmail(email, {
        paywallType: 'premium-feature-gate',
        screen: startState?.screen,
      });
    },
    [trackPaywallEmail, startState?.screen],
  );

  const country = userAccessBadge?.organization?.country;

  const component = (
    <PremiumFeatureGateModal
      visible={show}
      onClose={hide}
      country={country}
      onEmailSubmit={handleEmailSubmit}
    />
  );

  return {
    promptPaywall: (metadata?: PaywallMetadata) => prompt(metadata),
    hidePaywall: hide,
    paywallComponent: component,
    shouldPromptPaywall,
    isReady,
    isShowingPaywallPrompt: show,
    userAccessBadge,
    tenantName: userAccessBadge?.organization?.name,
  };
}

export type PaywallMetadata = {
  label?: string;
  description?: string;
  screen?: string;
  variant?: string;
};

export default usePaywallPrompt;
